// @ds-adherence-ignore -- omelette starter scaffold (raw elements/hex/px by design)
/* BEGIN USAGE */
/**
 * <image-slot> — user-fillable image placeholder.
 *
 * Drop this into a deck, mockup, or page wherever you want the user to
 * supply an image. You control the slot's shape and size; the user fills it
 * by dragging an image file onto it (or clicking to browse). The dropped
 * image persists across reloads via a .image-slots.state.json sidecar —
 * same read-via-fetch / write-via-window.omelette pattern as
 * design_canvas.jsx, so the filled slot shows on share links, downloaded
 * zips, and PPTX export. Outside the omelette runtime the slot is read-only.
 *
 * The host bridge only allows sidecar writes at the project root, so the
 * HTML that uses this component is assumed to live at the project root too
 * (same constraint as design_canvas.jsx).
 *
 * Attributes:
 *   id           Persistence key. REQUIRED for the drop to survive reload —
 *                every slot on the page needs a distinct id.
 *   shape        'rect' | 'rounded' | 'circle' | 'pill'   (default 'rounded')
 *                'circle' applies 50% border-radius; on a non-square slot
 *                that's an ellipse — set equal width and height for a true
 *                circle.
 *   radius       Corner radius in px for 'rounded'.       (default 12)
 *   mask         Any CSS clip-path value. Overrides `shape` — use this for
 *                hexagons, blobs, arbitrary polygons.
 *   fit          object-fit: cover | contain | fill.       (default 'cover')
 *                With cover (the default) double-clicking the filled slot
 *                enters a reframe mode: the whole image spills past the mask
 *                (translucent outside, opaque inside), drag to reposition,
 *                corner-drag to scale. The crop persists alongside the image
 *                in the sidecar. contain/fill stay static.
 *   position     object-position for fit=contain|fill.     (default '50% 50%')
 *   placeholder  Empty-state caption.                      (default 'Drop an image')
 *   src          Optional initial/fallback image URL. A user drop overrides
 *                it; clearing the drop reveals src again.
 *
 * Size and layout come from ordinary CSS on the element — width/height
 * inline or from a parent grid — so it composes with any layout.
 *
 * Usage:
 *   <image-slot id="hero"   style="width:800px;height:450px" shape="rounded" radius="20"
 *               placeholder="Drop a hero image"></image-slot>
 *   <image-slot id="avatar" style="width:120px;height:120px" shape="circle"></image-slot>
 *   <image-slot id="kite"   style="width:300px;height:300px"
 *               mask="polygon(50% 0, 100% 50%, 50% 100%, 0 50%)"></image-slot>
 */
/* END USAGE */

(() => {
  const STATE_FILE = '.image-slots.state.json';
  // 2× a ~600px slot in a 1920-wide deck — retina-sharp without making the
  // sidecar enormous. A 1200px WebP at q=0.85 is ~150-300KB.
  const MAX_DIM = 1200;
  // Raster formats only. SVG is excluded (can carry script; createImageBitmap
  // on SVG blobs is inconsistent). GIF is excluded because the canvas
  // re-encode keeps only the first frame, so an animated GIF would silently
  // go still — better to reject than surprise.
  const ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'image/avif'];

  // ── Shared sidecar store ────────────────────────────────────────────────
  // One fetch + immediate write-on-change for every <image-slot> on the
  // page. Reads via fetch() so viewing works anywhere the HTML and sidecar
  // are served together; writes go through window.omelette.writeFile, which
  // the host allowlists to *.state.json basenames only.
  const subs = new Set();
  let slots = {};
  // ids explicitly cleared before the sidecar fetch resolved — otherwise
  // the merge below can't tell "never set" from "just deleted" and would
  // resurrect the sidecar's stale value.
  const tombstones = new Set();
  let loaded = false;
  let loadP = null;

  function load() {
    if (loadP) return loadP;
    loadP = fetch(STATE_FILE)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        // Merge: sidecar loses to any in-memory change that raced ahead of
        // the fetch (drop or clear) so neither is clobbered by hydration.
        if (j && typeof j === 'object') {
          const merged = Object.assign({}, j, slots);
          // A framing-only write that raced ahead of hydration must not
          // drop a user image that's only on disk — inherit u from the
          // sidecar for any in-memory entry that lacks one.
          for (const k in slots) {
            if (merged[k] && !merged[k].u && j[k]) {
              merged[k].u = typeof j[k] === 'string' ? j[k] : j[k].u;
            }
          }
          for (const id of tombstones) delete merged[id];
          slots = merged;
        }
        tombstones.clear();
      })
      .catch(() => {})
      .then(() => { loaded = true; subs.forEach((fn) => fn()); });
    return loadP;
  }

  // Serialize writes so two near-simultaneous drops on different slots
  // can't reorder at the backend and leave the sidecar with only the
  // first. A save requested mid-flight just marks dirty and re-fires on
  // completion with the then-current slots.
  let saving = false;
  let saveDirty = false;
  function save() {
    if (saving) { saveDirty = true; return; }
    const w = window.omelette && window.omelette.writeFile;
    if (!w) return;
    saving = true;
    Promise.resolve(w(STATE_FILE, JSON.stringify(slots)))
      .catch(() => {})
      .then(() => { saving = false; if (saveDirty) { saveDirty = false; save(); } });
  }

  const S_MAX = 5;
  const clampS = (s) => Math.max(1, Math.min(S_MAX, s));

  // Normalize a stored slot value. Pre-reframe sidecars stored a bare
  // data-URL string; newer ones store {u, s, x, y}. Either shape is valid.
  function getSlot(id) {
    const v = slots[id];
    if (!v) return null;
    return typeof v === 'string' ? { u: v, s: 1, x: 0, y: 0 } : v;
  }

  function setSlot(id, val) {
    if (!id) return;
    if (val) { slots[id] = val; tombstones.delete(id); }
    else { delete slots[id]; if (!loaded) tombstones.add(id); }
    subs.forEach((fn) => fn());
    // A drop is rare + high-value — write immediately so nav-away can't lose
    // it. Gate on the initial read so we don't overwrite a sidecar we haven't
    // merged yet; the merge in load() keeps this change once the read lands.
    if (loaded) save(); else load().then(save);
  }

  // ── Image downscale ─────────────────────────────────────────────────────
  // Encode through a canvas so the sidecar carries resized bytes, not the
  // raw upload. Longest side is capped at 2× the slot's rendered width
  // (retina) and at MAX_DIM. WebP keeps alpha and is ~10× smaller than PNG
  // for photos, so there's no need for per-image format picking.
  async function toDataUrl(file, targetW) {
    const bitmap = await createImageBitmap(file);
    try {
      const cap = Math.min(MAX_DIM, Math.max(1, Math.round(targetW * 2)) || MAX_DIM);
      const scale = Math.min(1, cap / Math.max(bitmap.width, bitmap.height));
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      return canvas.toDataURL('image/webp', 0.85);
    } finally {
      bitmap.close && bitmap.close();
    }
  }

  // ── Custom element ──────────────────────────────────────────────────────
  const stylesheet =
    ':host{display:inline-block;position:relative;vertical-align:top;' +
    '  font:13px/1.3 system-ui,-apple-system,sans-serif;color:rgba(0,0,0,.55);width:240px;height:160px}' +
    '.frame{position:absolute;inset:0;overflow:hidden;background:rgba(0,0,0,.04)}' +
    // .frame img (clipped) and .spill (unclipped ghost + handles) share the
    // same left/top/width/height in frame-%, computed by _applyView(), so the
    // inside-mask crop and the outside-mask spill stay pixel-aligned.
    '.frame img{position:absolute;max-width:none;transform:translate(-50%,-50%);' +
    '  -webkit-user-drag:none;user-select:none;touch-action:none}' +
    // Reframe mode (double-click): the full image spills past the mask. The
    // spill layer is sized to the IMAGE bounds so its corners are where the
    // resize handles belong. The ghost <img> inside is translucent; the real
    // clipped <img> underneath shows the opaque in-mask crop.
    '.spill{position:absolute;transform:translate(-50%,-50%);display:none;z-index:1;' +
    '  cursor:grab;touch-action:none}' +
    ':host([data-panning]) .spill{cursor:grabbing}' +
    '.spill .ghost{position:absolute;inset:0;width:100%;height:100%;opacity:.35;' +
    '  pointer-events:none;-webkit-user-drag:none;user-select:none;' +
    '  box-shadow:0 0 0 1px rgba(0,0,0,.2),0 12px 32px rgba(0,0,0,.2)}' +
    '.spill .handle{position:absolute;width:12px;height:12px;border-radius:50%;' +
    '  background:#fff;box-shadow:0 0 0 1.5px #c96442,0 1px 3px rgba(0,0,0,.3);' +
    '  transform:translate(-50%,-50%)}' +
    '.spill .handle[data-c=nw]{left:0;top:0;cursor:nwse-resize}' +
    '.spill .handle[data-c=ne]{left:100%;top:0;cursor:nesw-resize}' +
    '.spill .handle[data-c=sw]{left:0;top:100%;cursor:nesw-resize}' +
    '.spill .handle[data-c=se]{left:100%;top:100%;cursor:nwse-resize}' +
    ':host([data-reframe]){z-index:10}' +
    ':host([data-reframe]) .spill{display:block}' +
    ':host([data-reframe]) .frame{box-shadow:0 0 0 2px #c96442}' +
    '.empty{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;' +
    '  justify-content:center;gap:6px;text-align:center;padding:12px;box-sizing:border-box;' +
    '  cursor:pointer;user-select:none}' +
    '.empty svg{opacity:.45}' +
    '.empty .cap{max-width:90%;font-weight:500;letter-spacing:.01em}' +
    '.empty .sub{font-size:11px}' +
    '.empty .sub u{text-underline-offset:2px;text-decoration-color:rgba(0,0,0,.25)}' +
    '.empty:hover .sub u{color:rgba(0,0,0,.75);text-decoration-color:currentColor}' +
    ':host([data-over]) .frame{outline:2px solid #c96442;outline-offset:-2px;' +
    '  background:rgba(201,100,66,.10)}' +
    '.ring{position:absolute;inset:0;pointer-events:none;border:1.5px dashed rgba(0,0,0,.25);' +
    '  transition:border-color .12s}' +
    ':host([data-over]) .ring{border-color:#c96442}' +
    ':host([data-filled]) .ring{display:none}' +
    // Controls sit BELOW the mask (top:100%), absolutely positioned so the
    // author-declared slot height is unaffected. The gap is padding, not a
    // top offset, so the hover target stays contiguous with the frame.
    '.ctl{position:absolute;top:100%;left:50%;transform:translateX(-50%);padding-top:8px;' +
    '  display:flex;gap:6px;opacity:0;pointer-events:none;transition:opacity .12s;z-index:2;' +
    '  white-space:nowrap}' +
    ':host([data-filled][data-editable]:hover) .ctl,:host([data-reframe]) .ctl' +
    '  {opacity:1;pointer-events:auto}' +
    '.ctl button{appearance:none;border:0;border-radius:6px;padding:5px 10px;cursor:pointer;' +
    '  background:rgba(0,0,0,.65);color:#fff;font:11px/1 system-ui,-apple-system,sans-serif;' +
    '  backdrop-filter:blur(6px)}' +
    '.ctl button:hover{background:rgba(0,0,0,.8)}' +
    '.err{position:absolute;left:8px;bottom:8px;right:8px;color:#b3261e;font-size:11px;' +
    '  background:rgba(255,255,255,.85);padding:4px 6px;border-radius:5px;pointer-events:none}';

  const icon =
    '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>' +
    '<path d="m21 15-5-5L5 21"/></svg>';

  class ImageSlot extends HTMLElement {
    static get observedAttributes() {
      return ['shape', 'radius', 'mask', 'fit', 'position', 'placeholder', 'src', 'id'];
    }

    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      // .spill and .ctl sit OUTSIDE .frame so overflow:hidden + border-radius
      // on the frame (circle, pill, rounded) can't clip them.
      root.innerHTML =
        '<style>' + stylesheet + '</style>' +
        '<div class="frame" part="frame">' +
        '  <img part="image" alt="" draggable="false" style="display:none">' +
        '  <div class="empty" part="empty">' + icon +
        '    <div class="cap"></div>' +
        '    <div class="sub">or <u>browse files</u></div></div>' +
        '  <div class="ring" part="ring"></div>' +
        '</div>' +
        '<div class="spill">' +
        '  <img class="ghost" alt="" draggable="false">' +
        '  <div class="handle" data-c="nw"></div><div class="handle" data-c="ne"></div>' +
        '  <div class="handle" data-c="sw"></div><div class="handle" data-c="se"></div>' +
        '</div>' +
        '<div class="ctl"><button data-act="replace" title="Replace image">Replace</button>' +
        '  <button data-act="clear" title="Remove image">Remove</button></div>' +
        '<input type="file" accept="' + ACCEPT.join(',') + '" hidden>';
      this._frame = root.querySelector('.frame');
      this._ring = root.querySelector('.ring');
      this._img = root.querySelector('.frame img');
      this._empty = root.querySelector('.empty');
      this._cap = root.querySelector('.cap');
      this._sub = root.querySelector('.sub');
      this._spill = root.querySelector('.spill');
      this._ghost = root.querySelector('.ghost');
      this._err = null;
      this._input = root.querySelector('input');
      this._depth = 0;
      this._gen = 0;
      this._view = { s: 1, x: 0, y: 0 };
      this._subFn = () => this._render();
      // Shadow-DOM listeners live with the shadow DOM — bound once here so
      // disconnect/reconnect (e.g. React remount) doesn't stack handlers.
      this._empty.addEventListener('click', () => this._input.click());
      root.addEventListener('click', (e) => {
        const act = e.target && e.target.getAttribute && e.target.getAttribute('data-act');
        if (act === 'replace') { this._exitReframe(true); this._input.click(); }
        if (act === 'clear') {
          this._exitReframe(false);
          this._gen++;
          this._local = null;
          if (this.id) setSlot(this.id, null); else this._render();
        }
      });
      this._input.addEventListener('change', () => {
        const f = this._input.files && this._input.files[0];
        if (f) this._ingest(f);
        this._input.value = '';
      });
      // naturalWidth/Height aren't known until load — re-apply so the cover
      // baseline is computed from real dimensions, not the 100%×100% fallback.
      this._img.addEventListener('load', () => this._applyView());
      // Gated on editable + fit=cover so share links and contain/fill slots
      // stay static.
      this.addEventListener('dblclick', (e) => {
        if (!this.hasAttribute('data-editable') || !this._reframes()) return;
        e.preventDefault();
        if (this.hasAttribute('data-reframe')) this._exitReframe(true);
        else this._enterReframe();
      });
      // Pan + resize both originate on the spill layer. A handle pointerdown
      // drives an aspect-locked resize anchored at the opposite corner; any
      // other pointerdown on the spill pans. Offsets are frame-% so a
      // reframed slot survives responsive resize / PPTX export.
      this._spill.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || !this.hasAttribute('data-reframe')) return;
        e.preventDefault();
        e.stopPropagation();
        this._spill.setPointerCapture(e.pointerId);
        const rect = this.getBoundingClientRect();
        const fw = rect.width || 1, fh = rect.height || 1;
        const corner = e.target.getAttribute && e.target.getAttribute('data-c');
        let move;
        if (corner) {
          // Resize about the OPPOSITE corner. Viewport-px throughout (rect
          // fw/fh, not clientWidth) so the math survives a transform:scale()
          // ancestor — deck_stage renders slides scaled-to-fit.
          const iw = this._img.naturalWidth || 1, ih = this._img.naturalHeight || 1;
          const base = Math.max(fw / iw, fh / ih);
          const sx = corner.includes('e') ? 1 : -1;
          const sy = corner.includes('s') ? 1 : -1;
          const s0 = this._view.s;
          const w0 = iw * base * s0, h0 = ih * base * s0;
          const cx0 = (50 + this._view.x) / 100 * fw;
          const cy0 = (50 + this._view.y) / 100 * fh;
          const ox = cx0 - sx * w0 / 2, oy = cy0 - sy * h0 / 2;
          const diag0 = Math.hypot(w0, h0);
          const ux = sx * w0 / diag0, uy = sy * h0 / diag0;
          move = (ev) => {
            const proj = (ev.clientX - rect.left - ox) * ux +
                         (ev.clientY - rect.top - oy) * uy;
            const s = clampS(s0 * proj / diag0);
            const d = diag0 * s / s0;
            this._view.s = s;
            this._view.x = (ox + ux * d / 2) / fw * 100 - 50;
            this._view.y = (oy + uy * d / 2) / fh * 100 - 50;
            this._clampView();
            this._applyView();
          };
        } else {
          this.setAttribute('data-panning', '');
          const start = { px: e.clientX, py: e.clientY, x: this._view.x, y: this._view.y };
          move = (ev) => {
            this._view.x = start.x + (ev.clientX - start.px) / fw * 100;
            this._view.y = start.y + (ev.clientY - start.py) / fh * 100;
            this._clampView();
            this._applyView();
          };
        }
        const up = () => {
          try { this._spill.releasePointerCapture(e.pointerId); } catch {}
          this._spill.removeEventListener('pointermove', move);
          this._spill.removeEventListener('pointerup', up);
          this._spill.removeEventListener('pointercancel', up);
          this.removeAttribute('data-panning');
          this._dragUp = null;
        };
        // Stashed so _exitReframe (Escape / outside-click mid-drag) can
        // tear the capture + listeners down synchronously.
        this._dragUp = up;
        this._spill.addEventListener('pointermove', move);
        this._spill.addEventListener('pointerup', up);
        this._spill.addEventListener('pointercancel', up);
      });
      // Wheel zoom stays available inside reframe mode as a trackpad nicety —
      // zooms toward the cursor (offset' = cursor·(1-k) + offset·k).
      this.addEventListener('wheel', (e) => {
        if (!this.hasAttribute('data-reframe')) return;
        e.preventDefault();
        const r = this.getBoundingClientRect();
        const cx = (e.clientX - r.left) / r.width * 100 - 50;
        const cy = (e.clientY - r.top) / r.height * 100 - 50;
        const prev = this._view.s;
        const next = clampS(prev * Math.pow(1.0015, -e.deltaY));
        if (next === prev) return;
        const k = next / prev;
        this._view.s = next;
        this._view.x = cx * (1 - k) + this._view.x * k;
        this._view.y = cy * (1 - k) + this._view.y * k;
        this._clampView();
        this._applyView();
      }, { passive: false });
    }

    connectedCallback() {
      // Warn once per page — an id-less slot works for the session but
      // cannot persist, and two id-less slots would share nothing.
      if (!this.id && !ImageSlot._warned) {
        ImageSlot._warned = true;
        console.warn('<image-slot> without an id will not persist its dropped image.');
      }
      this.addEventListener('dragenter', this);
      this.addEventListener('dragover', this);
      this.addEventListener('dragleave', this);
      this.addEventListener('drop', this);
      subs.add(this._subFn);
      // width%/height% in _applyView encode the frame aspect at call time —
      // a host resize (responsive grid, pane divider) would stretch the
      // image until the next _render. Re-render on size change: _render()
      // re-seeds _view from stored before clamp/apply, so a shrink→grow
      // cycle round-trips instead of ratcheting x/y toward the narrower
      // frame's clamp range.
      this._ro = new ResizeObserver(() => this._render());
      this._ro.observe(this);
      load();
      this._render();
    }

    disconnectedCallback() {
      subs.delete(this._subFn);
      this.removeEventListener('dragenter', this);
      this.removeEventListener('dragover', this);
      this.removeEventListener('dragleave', this);
      this.removeEventListener('drop', this);
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      this._exitReframe(false);
    }

    _enterReframe() {
      if (this.hasAttribute('data-reframe')) return;
      this.setAttribute('data-reframe', '');
      this._applyView();
      // Close on click outside (the spill handler stopPropagation()s so
      // in-image drags don't reach this) and on Escape. Listeners are held
      // on the instance so _exitReframe / disconnectedCallback can detach
      // exactly what was attached.
      this._outside = (e) => {
        if (e.composedPath && e.composedPath().includes(this)) return;
        this._exitReframe(true);
      };
      this._esc = (e) => { if (e.key === 'Escape') this._exitReframe(true); };
      document.addEventListener('pointerdown', this._outside, true);
      document.addEventListener('keydown', this._esc, true);
    }

    _exitReframe(commit) {
      if (!this.hasAttribute('data-reframe')) return;
      if (this._dragUp) this._dragUp();
      this.removeAttribute('data-reframe');
      this.removeAttribute('data-panning');
      if (this._outside) document.removeEventListener('pointerdown', this._outside, true);
      if (this._esc) document.removeEventListener('keydown', this._esc, true);
      this._outside = this._esc = null;
      if (commit) this._commitView();
    }

    attributeChangedCallback() { if (this.shadowRoot) this._render(); }

    // handleEvent — one listener object for all four drag events keeps the
    // add/remove symmetric and the depth counter correct.
    handleEvent(e) {
      if (e.type === 'dragenter' || e.type === 'dragover') {
        // Without preventDefault the browser never fires 'drop'.
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        if (e.type === 'dragenter') this._depth++;
        this.setAttribute('data-over', '');
      } else if (e.type === 'dragleave') {
        // dragenter/leave fire for every descendant crossing — count depth
        // so hovering the icon inside the empty state doesn't flicker.
        if (--this._depth <= 0) { this._depth = 0; this.removeAttribute('data-over'); }
      } else if (e.type === 'drop') {
        e.preventDefault();
        e.stopPropagation();
        this._depth = 0;
        this.removeAttribute('data-over');
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) this._ingest(f);
      }
    }

    async _ingest(file) {
      this._setError(null);
      if (!file || ACCEPT.indexOf(file.type) < 0) {
        this._setError('Drop a PNG, JPEG, WebP, or AVIF image.');
        return;
      }
      // toDataUrl can take hundreds of ms on a large photo. A Clear or a
      // newer drop during that window would be clobbered when this await
      // resumes — bump + capture a generation so stale encodes bail.
      const gen = ++this._gen;
      try {
        const w = this.clientWidth || this.offsetWidth || MAX_DIM;
        const url = await toDataUrl(file, w);
        if (gen !== this._gen) return;
        // Only exit reframe once the new image is in hand — a rejected type
        // or decode failure leaves the in-progress crop untouched.
        this._exitReframe(false);
        const val = { u: url, s: 1, x: 0, y: 0 };
        setSlot(this.id || '', val);
        // Keep a session-local copy for id-less slots so the drop still
        // shows, even though it cannot persist.
        if (!this.id) { this._local = val; this._render(); }
      } catch (err) {
        if (gen !== this._gen) return;
        this._setError('Could not read that image.');
        console.warn('<image-slot> ingest failed:', err);
      }
    }

    _setError(msg) {
      if (this._err) { this._err.remove(); this._err = null; }
      if (!msg) return;
      const d = document.createElement('div');
      d.className = 'err'; d.textContent = msg;
      this.shadowRoot.appendChild(d);
      this._err = d;
      setTimeout(() => { if (this._err === d) { d.remove(); this._err = null; } }, 3000);
    }

    // Reframing (pan/resize) is only meaningful for fit=cover — contain/fill
    // keep the old object-fit path and double-click is a no-op.
    _reframes() {
      return this.hasAttribute('data-filled') &&
        (this.getAttribute('fit') || 'cover') === 'cover';
    }

    // Cover-baseline geometry, shared by clamp/apply/resize. Null until the
    // img has loaded (naturalWidth is 0 before that) or when the slot has no
    // layout box — ResizeObserver fires with a 0×0 rect under display:none,
    // and clamping against a degenerate 1×1 frame would silently pull the
    // stored pan toward zero.
    _geom() {
      const iw = this._img.naturalWidth, ih = this._img.naturalHeight;
      const fw = this.clientWidth, fh = this.clientHeight;
      if (!iw || !ih || !fw || !fh) return null;
      return { iw, ih, fw, fh, base: Math.max(fw / iw, fh / ih) };
    }

    _clampView() {
      // Pan range on each axis is half the overflow past the frame edge.
      const g = this._geom();
      if (!g) return;
      const mx = Math.max(0, (g.iw * g.base * this._view.s / g.fw - 1) * 50);
      const my = Math.max(0, (g.ih * g.base * this._view.s / g.fh - 1) * 50);
      this._view.x = Math.max(-mx, Math.min(mx, this._view.x));
      this._view.y = Math.max(-my, Math.min(my, this._view.y));
    }

    _applyView() {
      const g = this._geom();
      const fit = this.getAttribute('fit') || 'cover';
      if (fit !== 'cover' || !g) {
        // Non-cover, or dimensions not known yet (before img load).
        this._img.style.width = '100%';
        this._img.style.height = '100%';
        this._img.style.left = '50%';
        this._img.style.top = '50%';
        this._img.style.objectFit = fit;
        this._img.style.objectPosition = this.getAttribute('position') || '50% 50%';
        return;
      }
      // Cover baseline: img fills the frame on its tighter axis at s=1, so
      // pan works immediately on the overflowing axis without zooming first.
      // Width/height and left/top are all frame-% — depends only on the
      // frame aspect ratio, so a responsive resize keeps the same crop. The
      // spill layer mirrors the same box so its corners = image corners.
      const k = g.base * this._view.s;
      const w = (g.iw * k / g.fw * 100) + '%';
      const h = (g.ih * k / g.fh * 100) + '%';
      const l = (50 + this._view.x) + '%';
      const t = (50 + this._view.y) + '%';
      this._img.style.width = w; this._img.style.height = h;
      this._img.style.left = l; this._img.style.top = t;
      this._img.style.objectFit = '';
      this._spill.style.width = w; this._spill.style.height = h;
      this._spill.style.left = l; this._spill.style.top = t;
    }

    _commitView() {
      const v = { s: this._view.s, x: this._view.x, y: this._view.y };
      if (this._userUrl) v.u = this._userUrl;
      // Framing-only (no u) persists too so an author-src slot remembers its
      // crop; clearing the sidecar still falls through to src=.
      if (this.id) setSlot(this.id, v);
      else { this._local = v; }
    }

    _render() {
      // Shape / mask. Presets use border-radius so the dashed ring can
      // follow the rounded outline; clip-path is only applied for an
      // explicit `mask` (the ring is hidden there since a rectangle
      // dashed border chopped by an arbitrary polygon looks broken).
      const mask = this.getAttribute('mask');
      const shape = (this.getAttribute('shape') || 'rounded').toLowerCase();
      let radius = '';
      if (shape === 'circle') radius = '50%';
      else if (shape === 'pill') radius = '9999px';
      else if (shape === 'rounded') {
        const n = parseFloat(this.getAttribute('radius'));
        radius = (Number.isFinite(n) ? n : 12) + 'px';
      }
      this._frame.style.borderRadius = mask ? '' : radius;
      this._frame.style.clipPath = mask || '';
      this._ring.style.borderRadius = mask ? '' : radius;
      this._ring.style.display = mask ? 'none' : '';

      // Controls and reframe entry gate on this so share links stay read-only.
      const editable = !!(window.omelette && window.omelette.writeFile);
      this.toggleAttribute('data-editable', editable);
      this._sub.style.display = editable ? '' : 'none';

      // Content. The sidecar is also writable by the agent's write_file
      // tool, so its value isn't guaranteed canvas-originated — only accept
      // data:image/ URLs from it. The `src` attribute is author-controlled
      // (Claude wrote it into the HTML) so it passes through unchanged.
      let stored = this.id ? getSlot(this.id) : this._local;
      if (stored && stored.u && !/^data:image\//i.test(stored.u)) stored = null;
      const srcAttr = this.getAttribute('src') || '';
      this._userUrl = (stored && stored.u) || null;
      const url = this._userUrl || srcAttr;
      // Don't clobber an in-flight reframe with a store-triggered re-render.
      if (!this.hasAttribute('data-reframe')) {
        this._view = {
          s: stored && Number.isFinite(stored.s) ? clampS(stored.s) : 1,
          x: stored && Number.isFinite(stored.x) ? stored.x : 0,
          y: stored && Number.isFinite(stored.y) ? stored.y : 0,
        };
      }
      this._cap.textContent = this.getAttribute('placeholder') || 'Drop an image';
      // Toggle via style.display — the [hidden] attribute alone loses to
      // the display:flex / display:block rules in the stylesheet above.
      if (url) {
        if (this._img.getAttribute('src') !== url) {
          this._img.src = url;
          this._ghost.src = url;
        }
        this._img.style.display = 'block';
        this._empty.style.display = 'none';
        this.setAttribute('data-filled', '');
        this._clampView();
        this._applyView();
      } else {
        this._img.style.display = 'none';
        this._img.removeAttribute('src');
        this._ghost.removeAttribute('src');
        this._empty.style.display = 'flex';
        this.removeAttribute('data-filled');
      }
    }
  }

  if (!customElements.get('image-slot')) {
    customElements.define('image-slot', ImageSlot);
  }
})();



  /* ── fade-up scroll observer ── */
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('in');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.1 });
  document.querySelectorAll('.fade-up').forEach(el => io.observe(el));

  /* ── pain lines stagger ── */
  const painLines = document.querySelectorAll('.pain__lines p');
  const painIO = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      painLines.forEach((l, i) => {
        setTimeout(() => l.classList.add('visible'), i * 210);
      });
      painIO.disconnect();
    }
  }, { threshold: 0.25 });
  if (painLines.length) painIO.observe(painLines[0].closest('.pain__lines'));



(function(){
  /* ── Scale ── */
  function applyScale(){
    var w=document.getElementById('scaleWrap');
    var ip=document.getElementById('infoPanel');
    if(!w||!ip) return;
    var stacked = window.matchMedia('(max-width:700px)').matches;
    var availW;
    if(stacked){
      // 手機：用區段內容寬（避免被固定 420px 撐大的欄位誤導）
      var sec=document.getElementById('overview');
      if(sec){
        var cs=getComputedStyle(sec);
        availW=sec.clientWidth-parseFloat(cs.paddingLeft)-parseFloat(cs.paddingRight);
      } else { availW=window.innerWidth-32; }
    } else {
      var left=document.querySelector('.panel-left');
      availW=left?left.offsetWidth:window.innerWidth;
    }
    var s=Math.min(availW, stacked?420:504)/420;
    if(stacked){
      w.style.transformOrigin='top left';
    } else {
      w.style.transformOrigin='top center';
    }
    w.style.transform='scale('+s+')';
    w.style.marginBottom=(315*s-315)+'px';
    // info panel 永遠不縮放、100% 填滿欄位
    ip.style.transform='none';
    ip.style.marginBottom='0';
    ip.style.width='100%';
  }
  applyScale();
  window.addEventListener('resize',applyScale);

  /* ── Knob refs ── */
  var kn=document.getElementById('kn');
  var kGreen=document.getElementById('k-green');
  var kTop=document.getElementById('k-top');
  var ring=document.getElementById('charge-ring');
  var ringCtx=ring.getContext('2d');
  var knobOn=false,isDragging=false;

  function updateKnob(){ kGreen.style.opacity='1'; kTop.style.opacity=isDragging?'0':'1'; }

  /* ── Charge ring ── */
  var LONG_KNOB=700,chargeRaf=null,chargeStart=null;
  function drawCharge(pct){
    var W=ring.width,H=ring.height;
    ringCtx.clearRect(0,0,W,H);
    if(pct<=0)return;
    ringCtx.beginPath();
    ringCtx.arc(W/2,H/2,Math.min(W,H)/2-4,-Math.PI/2,-Math.PI/2+Math.PI*2*(pct/100),false);
    ringCtx.strokeStyle='rgba(61,196,122,0.9)';
    ringCtx.lineWidth=3; ringCtx.lineCap='round'; ringCtx.stroke();
  }
  function startCharge(){
    chargeStart=performance.now(); ring.style.opacity='1';
    (function tick(){ var p=Math.min(100,(performance.now()-chargeStart)/LONG_KNOB*100);
      drawCharge(p); if(p<100) chargeRaf=requestAnimationFrame(tick); })();
  }
  function stopCharge(){
    if(chargeRaf){cancelAnimationFrame(chargeRaf);chargeRaf=null;}
    ring.style.opacity='0'; drawCharge(0);
  }

  /* ── Status ── */
  var active=null,st=document.getElementById('st');
  function setSt(t,on){st.textContent=t;st.className='status'+(on?' on':'');}
  var msgs={bv:'VOL  ACTIVE',be:'EQ   ACTIVE',bc:'COMP ACTIVE',br:'REV  ACTIVE'};

  /* ── Info data ── */
  var infos={
    bv:{title:'VOL：音量／監聽',body:'按下 VOL 鍵，轉動旋鈕即可調整麥克風與監聽耳機的音量。調整完成後，按一下旋鈕即可回到主畫面，或繼續使用其他調整功能。'},
    be:{title:'EQ — 等化器',body:'按下 EQ 鍵進入等化器模式。轉動旋鈕切換選項：OFF / FLAT / L CUT / H CUT / BOTH / MY EQ。按一下旋鈕確認並回到主畫面。<br><br><span style="font-family:var(--t-mono);font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#888;">MY EQ 參數</span><br><table style="width:100%;margin-top:6px;border-collapse:collapse;font-size:12px;line-height:1.8;"><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Low Shelf</td><td style="color:#111;font-weight:500;">100 Hz</td><td style="padding:2px 8px 2px 16px;color:#888;white-space:nowrap;">低頻 Bass</td><td style="color:#111;font-weight:500;">250–500 Hz</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">低中頻</td><td style="color:#111;font-weight:500;">600–900 Hz</td><td style="padding:2px 8px 2px 16px;color:#888;white-space:nowrap;">高中頻</td><td style="color:#111;font-weight:500;">1K–2.5K</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">高頻 Treble</td><td style="color:#111;font-weight:500;">3K–5K</td><td style="padding:2px 8px 2px 16px;color:#888;white-space:nowrap;">High Shelf</td><td style="color:#111;font-weight:500;">8K</td></tr></table>'},
    bc:{title:'COMP — 壓縮器',body:'按下 COMP 鍵進入壓縮模式。轉動旋鈕切換選項：OFF / LOW / MID / HIGH / MY COMP。按一下旋鈕確認並回到主畫面。<br><br><span style="font-family:var(--t-mono);font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#888;">MY COMP 參數</span><br><table style="width:100%;margin-top:6px;border-collapse:collapse;font-size:12px;line-height:1.8;"><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Threshold 門檻</td><td style="color:#111;font-weight:500;">觸發音量臨界點</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Ratio 比例</td><td style="color:#111;font-weight:500;">壓縮強度</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Gain 增益</td><td style="color:#111;font-weight:500;">輸出音量補償</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Attack 起音</td><td style="color:#111;font-weight:500;">啟動反應速度</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Release 釋音</td><td style="color:#111;font-weight:500;">釋放反應速度</td></tr></table>'},
    br:{title:'REV — 殘響',body:'按下 REV 鍵進入殘響模式。轉動旋鈕切換選項：OFF / S ROOM / S HALL / L HALL / MY REV。按一下旋鈕確認並回到主畫面。<br><br><span style="font-family:var(--t-mono);font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#888;">MY REV 參數</span><br><table style="width:100%;margin-top:6px;border-collapse:collapse;font-size:12px;line-height:1.8;"><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Type 類型</td><td style="color:#111;font-weight:500;">Room / Hall / Chamber / Plate</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Tone 音色</td><td style="color:#111;font-weight:500;">殘響尾韻的明亮度</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Space 空間</td><td style="color:#111;font-weight:500;">空間大小與衰減</td></tr><tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap;">Blend 混合</td><td style="color:#111;font-weight:500;">乾濕聲比例</td></tr></table>'}
  };
  var defaultDesc={title:'主顯示螢幕',body:'即時顯示麥克風、外部音源與總輸出的訊號強度。請依照螢幕上標示的兩段範圍調整各自的音量強度，以獲得最佳音質。'};

  var rowIds=['row-bv','row-be'];
  var btnIds=['bv','be','bc','br'];

  function syncInfoPanel(activeId){
    var title=document.getElementById('descTitle');
    var body=document.getElementById('descBody');
    rowIds.forEach(function(rid,i){
      var row=document.getElementById(rid);
      var bid=btnIds[i];
      row.classList.remove('active','dimmed');
      if(activeId===null) row.classList.add('dimmed');
      else if(bid===activeId) row.classList.add('active');
      else row.classList.add('dimmed');
    });
    if(activeId&&infos[activeId]){ title.textContent=infos[activeId].title; body.innerHTML=infos[activeId].body; }
    else { title.textContent=defaultDesc.title; body.innerHTML=defaultDesc.body; }
  }

  /* ── LCD images ── */
  var LCD={
    default:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/FT15_default.jpg?v=1781684541',
    vol1:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/FT15_MIc_level.jpg?v=1781685336',
    vol2:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/FT15_Monitor_level.jpg?v=1781685344',
    eq_off:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/EQ-OFF.png?v=1779251941',
    eq_flat:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/EQ-FLAT.png?v=1779251840',
    eq_lcut:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/EQ-L_CUT.png?v=1779251840',
    eq_hcut:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/EQ-H_CUT.png?v=1779251840',
    eq_both:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/EQ-Both.png?v=1779251840',
    eq_myeq:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/EQ-MY_EQ.png?v=1779251840',
    myeq_setting:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/FT15_EQ-_EQ_100h.jpg?v=1779422417',
    comp_off:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/COMP-OFF.png?v=1779698785',
    comp_low:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/COMP-LOW.png?v=1779698775',
    comp_mid:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/COMP-MID.png?v=1779698803',
    comp_high:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/COMP-HIGH.png?v=1779698792',
    comp_mycomp:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/COMP-MY_COMP.png?v=1779252768',
    mycomp_thld:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/MY_COMP-_My_THLD.png?v=1779431273',
    mycomp_ratio:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/MY_COMP-_My_RATIO.png?v=1779431273',
    mycomp_gain:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/MY_COMP-_My_GAIN.png?v=1779431273',
    mycomp_atk:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/MY_COMP-_My_atk_t.png?v=1779431273',
    mycomp_rls:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/MY_COMP-_My_rls_t.png?v=1779431273',
    rev_off:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-OFF.png?v=1779266677',
    rev_sroom:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-S_ROOM.png?v=1779266677',
    rev_shall:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-S_HALL.png?v=1779266677',
    rev_lhall:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-LHALL.png?v=1779266677',
    rev_myrev:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-MY_REV.png?v=1779266677',
    myrev_room:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-TypeSel-ROOM.png?v=1779436853',
    myrev_hall:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-TypeSel-HALL.png?v=1779436853',
    myrev_chamber:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-TypeSel-CHAMBER.png?v=1779436853',
    myrev_plate:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/REV-TypeSel-PLATE.png?v=1779436853',
    ir_off:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/IR-OFF.png?v=1779336160',
    ir_reb:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/IR-RBN.png?v=1779336160',
    ir_dyn:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/IR-DYN.png?v=1779336160',
    loop_off:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/LOOP_BACK_OFF.png?v=1779336159',
    loop_on:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/LOOP_BACK_ON.png?v=1779336160',
    ng_off:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/Noise_gate_off.png?v=1779337204',
    ng_low:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/Noise_gate_LOW.png?v=1779337204',
    ng_mid:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/Noise_gate_MID.png?v=1779337204',
    ng_high:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/Noise_gate_HIGH.png?v=1779337204',
    ng_mygate:'https://cdn.shopify.com/s/files/1/0722/0871/1999/files/Noise_gate_MY_GATE.png?v=1779337204'
  };

  var EQ_OPT=['eq_off','eq_flat','eq_lcut','eq_hcut','eq_both','eq_myeq'];
  var EQ_LBL=['OFF','FLAT','L CUT','H CUT','BOTH','MY EQ'];
  var COMP_OPT=['comp_off','comp_low','comp_mid','comp_high','comp_mycomp'];
  var COMP_LBL=['OFF','LOW','MID','HIGH','MY COMP'];
  var MYCOMP_OPT=['mycomp_thld','mycomp_ratio','mycomp_gain','mycomp_atk','mycomp_rls'];
  var MYCOMP_LBL=['THRESHOLD','RATIO','GAIN','ATTACK TIME','RELEASE TIME'];
  var REV_OPT=['rev_off','rev_sroom','rev_shall','rev_lhall','rev_myrev'];
  var REV_LBL=['OFF','S ROOM','S HALL','L HALL','MY REV'];
  var MYREV_OPT=['myrev_room','myrev_hall','myrev_chamber','myrev_plate'];
  var MYREV_LBL=['ROOM','HALL','CHAMBER','PLATE'];
  var LOGO_MODES=['ir','loop'];
  var LOGO_LBL=['IR','LOOP BACK'];
  var logoModeIdx=1; // start at last so first press goes to 0 (IR)
  var IR_OPT=['ir_off','ir_reb','ir_dyn'];
  var IR_LBL=['OFF','REB','DYN'];
  var LOOP_OPT=['loop_off','loop_on'];
  var LOOP_LBL=['OFF','ON'];
  var NG_OPT=['ng_off','ng_low','ng_mid','ng_high','ng_mygate'];
  var NG_LBL=['OFF','LOW','MID','HIGH','MY GATE'];
  var irIdx=0,loopIdx=0,ngIdx=0;

  var eqIdx=0,compIdx=0,myCompIdx=0,revIdx=0,myRevIdx=0;
  var myEqMode=false,myCompMode=false,myRevMode=false;
  var volPressCount=0,knobTarget=null;
  var lcdImg=document.getElementById('lcdImg');
  var psImg=document.getElementById('panelScreenImg');
  var barVals={bv:8,be:8};

  function setLCD(src){ lcdImg.src=src; psImg.src=src; }
  function updateBar(id,val){
    val=Math.max(1,Math.min(15,val)); barVals[id]=val;
    var f=document.getElementById('fill-'+id);
    var v=document.getElementById('val-'+id);
    if(f) f.style.width=((val/15)*100)+'%';
    if(v) v.textContent=val>=15?'MAX':val<=1?'MIN':val;
  }
  updateBar('bv',8); updateBar('be',8);

  /* ── Gray buttons ── */
  var LONG_BTN=600;
  ['bv','be','bc','br'].forEach(function(id){
    var el=document.getElementById(id);
    var timer=null,longFired=false;

    el.addEventListener('pointerdown',function(e){
      e.preventDefault(); el.classList.add('pressed');
      longFired=false;
      timer=setTimeout(function(){
        longFired=true;
        if(id==='be'&&knobTarget==='eq'&&EQ_LBL[eqIdx]==='MY EQ'){
          myEqMode=!myEqMode;
          setLCD(myEqMode?LCD.myeq_setting:LCD.eq_myeq);
          setSt(myEqMode?'● MY EQ SETTING':'● EQ: MY EQ',true); return;
        }
        if(id==='bc'&&knobTarget==='comp'&&COMP_LBL[compIdx]==='MY COMP'){
          myCompMode=!myCompMode; myCompIdx=0;
          setLCD(myCompMode?LCD[MYCOMP_OPT[0]]:LCD.comp_mycomp);
          setSt(myCompMode?'● '+MYCOMP_LBL[0]:'● COMP: MY COMP',true); return;
        }
        if(id==='br'&&knobTarget==='rev'&&REV_LBL[revIdx]==='MY REV'){
          myRevMode=!myRevMode; myRevIdx=0;
          setLCD(myRevMode?LCD[MYREV_OPT[0]]:LCD.rev_myrev);
          setSt(myRevMode?'● '+MYREV_LBL[0]:'● REV: MY REV',true); return;
        }
        if(active){document.getElementById(active).classList.remove('on');active=null;}
        el.classList.remove('on'); setSt('◉ '+msgs[id].replace('ACTIVE','LONG'),true);
      },LONG_BTN);
    });

    el.addEventListener('pointerup',function(){
      el.classList.remove('pressed');
      if(timer){clearTimeout(timer);timer=null;}
      if(!longFired){
        if(id==='bv'){
          if(active&&active!=='bv') document.getElementById(active).classList.remove('on');
          el.classList.add('on'); active='bv'; setSt('● '+msgs['bv'],true);
          syncInfoPanel('bv');
          volPressCount=(volPressCount%2)+1;
          setLCD(volPressCount===1?LCD.vol1:LCD.vol2);
          knobTarget=volPressCount===1?'bv':'be';
          document.getElementById('row-bv').classList.toggle('active',knobTarget==='bv');
          document.getElementById('row-bv').classList.toggle('dimmed',knobTarget!=='bv');
          document.getElementById('row-be').classList.toggle('active',knobTarget==='be');
          document.getElementById('row-be').classList.toggle('dimmed',knobTarget!=='be');
        } else {
          if(active===id){
            if(id==='bc'&&myCompMode){
              myCompIdx=(myCompIdx+1)%MYCOMP_OPT.length;
              setLCD(LCD[MYCOMP_OPT[myCompIdx]]); setSt('● '+MYCOMP_LBL[myCompIdx],true);
            } else {
              el.classList.remove('on'); active=null; setSt('— STANDBY —',false);
              syncInfoPanel(null); knobTarget=null;
              if(id==='bc'){myCompMode=false;myCompIdx=0;}
              if(id==='br'){myRevMode=false;myRevIdx=0;}
              setLCD(LCD.default);
            }
          } else {
            if(active) document.getElementById(active).classList.remove('on');
            el.classList.add('on'); active=id; setSt('● '+msgs[id],true);
            syncInfoPanel(id);
            if(id==='be'){ setLCD(LCD[EQ_OPT[eqIdx]]); setSt('● EQ: '+EQ_LBL[eqIdx],true); knobTarget='eq'; }
            if(id==='bc'){ setLCD(LCD[COMP_OPT[compIdx]]); setSt('● COMP: '+COMP_LBL[compIdx],true); knobTarget='comp'; }
            if(id==='br'){ setLCD(LCD[REV_OPT[revIdx]]); setSt('● REV: '+REV_LBL[revIdx],true); knobTarget='rev'; }
          }
        }
      }
    });
    el.addEventListener('pointercancel',function(){ el.classList.remove('pressed'); if(timer){clearTimeout(timer);timer=null;} });
  });

  /* ── Green knob ── */
  var ang=0,didDrag=false,sa=0,ao=0,knobTimer=null,knobLong=false;
  window._lastAng=0; window._angAcc=0;

  function getA(e,el){
    var r=el.getBoundingClientRect();
    var pt=e.touches?e.touches[0]:e;
    return Math.atan2(pt.clientY-(r.top+r.height/2),pt.clientX-(r.left+r.width/2))*180/Math.PI;
  }

  kn.addEventListener('pointerdown',function(e){
    e.preventDefault(); kn.setPointerCapture(e.pointerId);
    sa=getA(e,kn); ao=ang; didDrag=false; knobLong=false;
    window._lastAng=ang; window._angAcc=0;
    kn.classList.add('green-pressed'); startCharge();
    knobTimer=setTimeout(function(){
      if(!didDrag){
        knobLong=true; stopCharge(); ang=0;
        kGreen.style.transform='rotate(0deg)';
        isDragging=false; knobOn=false;
        kn.classList.remove('on-state','green-pressed');
        updateKnob(); setSt('◉ GAIN RESET',true);
      }
    },LONG_KNOB);
  });

  kn.addEventListener('pointermove',function(e){
    if(!kn.hasPointerCapture(e.pointerId)) return;
    var a=getA(e,kn),delta=a-sa;
    if(delta>180) delta-=360; if(delta<-180) delta+=360;
    if(Math.abs(delta)>1.5){
      if(!didDrag){ didDrag=true; stopCharge(); if(knobTimer){clearTimeout(knobTimer);knobTimer=null;} kn.classList.remove('green-pressed'); }
      isDragging=true; kn.classList.add('dragging');
      ang=ao+delta; kGreen.style.transform='rotate('+ang+'deg)'; updateKnob();

      var dDeg=ang-window._lastAng; window._lastAng=ang; window._angAcc+=dDeg;
      var steps=Math.trunc(window._angAcc/30);
      if(steps!==0){ window._angAcc-=steps*30;
        if(knobTarget==='eq'){
          eqIdx=(eqIdx+steps%EQ_OPT.length+EQ_OPT.length)%EQ_OPT.length;
          setLCD(LCD[EQ_OPT[eqIdx]]); setSt('● EQ: '+EQ_LBL[eqIdx],true);
        } else if(knobTarget==='comp'){
          if(myCompMode){ myCompIdx=(myCompIdx+steps%MYCOMP_OPT.length+MYCOMP_OPT.length)%MYCOMP_OPT.length; setLCD(LCD[MYCOMP_OPT[myCompIdx]]); setSt('● '+MYCOMP_LBL[myCompIdx],true); }
          else { compIdx=(compIdx+steps%COMP_OPT.length+COMP_OPT.length)%COMP_OPT.length; setLCD(LCD[COMP_OPT[compIdx]]); setSt('● COMP: '+COMP_LBL[compIdx],true); }
        } else if(knobTarget==='rev'){
          if(myRevMode){ myRevIdx=(myRevIdx+steps%MYREV_OPT.length+MYREV_OPT.length)%MYREV_OPT.length; setLCD(LCD[MYREV_OPT[myRevIdx]]); setSt('● '+MYREV_LBL[myRevIdx],true); }
          else { revIdx=(revIdx+steps%REV_OPT.length+REV_OPT.length)%REV_OPT.length; setLCD(LCD[REV_OPT[revIdx]]); setSt('● REV: '+REV_LBL[revIdx],true); }
        } else if(knobTarget==='ir'){
          irIdx=(irIdx+steps%IR_OPT.length+IR_OPT.length)%IR_OPT.length;
          setLCD(LCD[IR_OPT[irIdx]]); setSt('● IR: '+IR_LBL[irIdx],true);
        } else if(knobTarget==='loop'){
          loopIdx=(loopIdx+steps%LOOP_OPT.length+LOOP_OPT.length)%LOOP_OPT.length;
          setLCD(LCD[LOOP_OPT[loopIdx]]); setSt('● LOOP BACK: '+LOOP_LBL[loopIdx],true);
        } else if(knobTarget==='ng'){
          ngIdx=(ngIdx+steps%NG_OPT.length+NG_OPT.length)%NG_OPT.length;
          setLCD(LCD[NG_OPT[ngIdx]]); setSt('● NOISE GATE: '+NG_LBL[ngIdx],true);
        } else if(knobTarget==='bv'||knobTarget==='be'){
          updateBar(knobTarget,barVals[knobTarget]+steps);
          var label=knobTarget==='bv'?'MIC LVL':'MONITOR';
          var v=barVals[knobTarget];
          setSt('● '+label+' '+(v>=15?'MAX':v<=1?'MIN':v),true);
        }
      }
    }
  });

  kn.addEventListener('pointerup',function(e){
    kn.releasePointerCapture(e.pointerId);
    stopCharge(); kn.classList.remove('green-pressed','dragging');
    if(knobTimer){clearTimeout(knobTimer);knobTimer=null;}
    // 純點擊（沒有拖曳、沒有長按）→ 回到 default 主畫面
    if(!didDrag && !knobLong){
      if(knobTarget){
        // 清掉作用中的 EQ/COMP/REV 按鈕高亮
        if(active){ document.getElementById(active).classList.remove('on'); active=null; }
        // 清掉 IR/LOOP 模式時銀色按鈕高亮
        sb.classList.remove('active'); logoModeIdx=LOGO_MODES.length-1;
        // 重置 MY 子模式
        myEqMode=false; myCompMode=false; myCompIdx=0; myRevMode=false; myRevIdx=0;
        knobTarget=null;
        setSt('— STANDBY —',false);
        syncInfoPanel(null);
        setLCD(LCD.default);
        ['row-bv','row-be'].forEach(function(r){
          document.getElementById(r).classList.remove('active');
          document.getElementById(r).classList.add('dimmed');
        });
      }
    }
    isDragging=false; didDrag=false; knobLong=false; updateKnob();
  });
  kn.addEventListener('pointercancel',function(){ stopCharge(); kn.classList.remove('green-pressed','dragging'); isDragging=false; updateKnob(); });

  /* ── Silver button — cycles IR / LOOP BACK / NOISE GATE ── */
  var sb=document.getElementById('silverBtn');
  var silverTimer=null, silverLong=false;
  sb.addEventListener('pointerdown',function(e){
    e.preventDefault(); sb.classList.add('pressed'); silverLong=false;
    silverTimer=setTimeout(function(){
      silverLong=true;
      sb.classList.remove('pressed','active');
      logoModeIdx=LOGO_MODES.length-1; knobTarget=null;
      setSt('◉ POWER RESET',true);
      syncInfoPanel(null); setLCD(LCD.default);
    },700);
  });
  sb.addEventListener('pointerup',function(){
    sb.classList.remove('pressed');
    if(silverTimer){clearTimeout(silverTimer);silverTimer=null;}
    if(!silverLong){
      // clear any active EQ/COMP/REV button
      if(active){ document.getElementById(active).classList.remove('on'); active=null; }
      logoModeIdx=(logoModeIdx+1)%LOGO_MODES.length;
      var mode=LOGO_MODES[logoModeIdx];
      sb.classList.add('active');
      if(mode==='ir'){ knobTarget='ir'; setLCD(LCD[IR_OPT[irIdx]]); setSt('● IR: '+IR_LBL[irIdx],true); }
      else if(mode==='loop'){ knobTarget='loop'; setLCD(LCD[LOOP_OPT[loopIdx]]); setSt('● LOOP BACK: '+LOOP_LBL[loopIdx],true); }
      else if(mode==='ng'){ knobTarget='ng'; setLCD(LCD[NG_OPT[ngIdx]]); setSt('● NOISE GATE: '+NG_LBL[ngIdx],true); }
    }
    silverLong=false;
  });
  sb.addEventListener('pointercancel',function(){
    sb.classList.remove('pressed');
    if(silverTimer){clearTimeout(silverTimer);silverTimer=null;}
    silverLong=false;
  });
})();



(function(){
  var deck=document.getElementById('tdeck');
  if(!deck) return;
  var cards=[].slice.call(deck.querySelectorAll('.tcard'));
  var n=cards.length, front=0, timer=null;
  var dotsWrap=document.getElementById('tdots');
  var dots=[];
  cards.forEach(function(_,i){
    var d=document.createElement('button');
    d.className='tcar__dot'; d.setAttribute('aria-label','第 '+(i+1)+' 則');
    d.addEventListener('click',function(){ front=i; render(); restart(); });
    dotsWrap.appendChild(d); dots.push(d);
  });
  function render(){
    cards.forEach(function(c,i){
      var rel=(i-front+n)%n;
      c.classList.remove('is-front','is-next','is-prev');
      if(rel===0) c.classList.add('is-front');
      else if(rel===1) c.classList.add('is-next');
      else c.classList.add('is-prev');
    });
    dots.forEach(function(d,i){ d.classList.toggle('on', i===front); });
  }
  function go(dir){ front=(front+dir+n)%n; render(); restart(); }
  function restart(){
    clearInterval(timer);
    // 尊重「減少動態」偏好：不自動輪播
    if(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    timer=setInterval(function(){ go(1); }, 5000);
  }
  document.getElementById('tnext').addEventListener('click',function(){ go(1); });
  document.getElementById('tprev').addEventListener('click',function(){ go(-1); });
  // click a back card to bring it forward
  cards.forEach(function(c){
    c.addEventListener('click',function(){
      if(c.classList.contains('is-next')) go(1);
      else if(c.classList.contains('is-prev')) go(-1);
    });
  });
  // touch swipe (mobile)
  var tsX=0, tsY=0, swiping=false;
  deck.addEventListener('touchstart',function(e){
    var t=e.changedTouches[0]; tsX=t.clientX; tsY=t.clientY; swiping=true;
    clearInterval(timer);
  },{passive:true});
  deck.addEventListener('touchend',function(e){
    if(!swiping) return; swiping=false;
    var t=e.changedTouches[0];
    var dx=t.clientX-tsX, dy=t.clientY-tsY;
    if(Math.abs(dx)>40 && Math.abs(dx)>Math.abs(dy)){
      go(dx<0?1:-1);   // swipe left → next, swipe right → prev
    } else {
      restart();
    }
  },{passive:true});
  // pause on hover
  deck.addEventListener('mouseenter',function(){ clearInterval(timer); });
  deck.addEventListener('mouseleave',restart);
  render(); restart();
})();
